import React, { useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
  GoogleAuthProvider,
  signInWithPopup
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import VeylLogo from './VeylLogo';
import { useLanguage } from '../i18n';

const styleTag = `
  @keyframes fadeInUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
  @keyframes float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-12px); }
  }
  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
  .auth-input {
    transition: border-color 0.2s, box-shadow 0.2s !important;
  }
  .auth-input:focus {
    border-color: #008081 !important;
    box-shadow: 0 0 0 3px rgba(0,128,129,0.15) !important;
    outline: none !important;
  }
  .auth-btn-primary {
    transition: transform 0.2s, box-shadow 0.2s !important;
  }
  .auth-btn-primary:hover:not(:disabled) {
    transform: translateY(-1px) !important;
    box-shadow: 0 8px 20px rgba(0,128,129,0.4) !important;
  }
  .auth-google-btn {
    transition: background-color 0.2s, transform 0.2s, box-shadow 0.2s !important;
  }
  .auth-google-btn:hover:not(:disabled) {
    background-color: rgba(255,255,255,0.12) !important;
    transform: translateY(-1px) !important;
    box-shadow: 0 4px 15px rgba(0,0,0,0.3) !important;
  }
  .auth-link:hover {
    color: #00c8c8 !important;
  }
  .auth-tab:hover {
    background-color: rgba(0,128,129,0.08) !important;
  }
`;

function getFriendlyError(err, t) {
  const code = err.code || '';
  if (code.includes('user-not-found') || code.includes('wrong-password') || code.includes('invalid-credential')) {
    return t('errorInvalidCredentials') || 'Incorrect email or password.';
  }
  if (code.includes('email-already-in-use')) {
    return t('errorEmailInUse') || 'An account with this email already exists.';
  }
  if (code.includes('weak-password')) {
    return t('errorWeakPassword') || 'Password must be at least 8 characters.';
  }
  if (code.includes('invalid-email')) {
    return t('errorInvalidEmail') || 'Please enter a valid email address.';
  }
  if (code.includes('too-many-requests')) {
    return t('errorTooManyRequests') || 'Too many attempts. Please try again later.';
  }
  if (code.includes('network-request-failed')) {
    return t('errorNetwork') || 'Network error. Please check your connection.';
  }
  if (code.includes('popup-closed-by-user') || code.includes('cancelled-popup-request')) {
    return '';
  }
  return t('errorGeneral') || 'Something went wrong. Please try again.';
}

export default function AuthPage({ onShowLanding }) {
  const { t } = useLanguage();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        await setDoc(doc(db, 'users', userCredential.user.uid), {
          email,
          createdAt: serverTimestamp(),
          onboardingComplete: false,
          partnerId: null,
          isPremium: false
        });
      }
    } catch (err) {
      setError(getFriendlyError(err, t));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      const userRef = doc(db, 'users', result.user.uid);
      const userSnap = await getDoc(userRef);

      // Only create the user doc if it doesn't exist yet (new Google user)
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          email: result.user.email,
          createdAt: serverTimestamp(),
          onboardingComplete: false,
          partnerId: null,
          isPremium: false
        });
      }
      // MainApp's onAuthStateChanged will handle routing automatically
    } catch (err) {
      const msg = getFriendlyError(err, t);
      if (msg) setError(msg);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!resetEmail.trim()) return;
    setResetLoading(true);
    setError('');
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetSent(true);
    } catch (err) {
      setError(getFriendlyError(err, t));
    } finally {
      setResetLoading(false);
    }
  };

  const bgStyle = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#080f1a',
    padding: '1rem',
    position: 'relative',
    overflow: 'hidden',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
  };

  const inputStyle = {
    width: '100%',
    padding: '0.875rem 1rem',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '0.75rem',
    fontSize: '0.95rem',
    boxSizing: 'border-box',
    backgroundColor: 'rgba(255,255,255,0.05)',
    color: '#ffffff',
    fontFamily: 'inherit',
  };

  // Google Sign-In button
  const GoogleButton = () => (
    <button
      type="button"
      className="auth-google-btn"
      onClick={handleGoogleSignIn}
      disabled={googleLoading || loading}
      style={{
        width: '100%',
        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem',
        padding: '0.875rem',
        backgroundColor: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.12)',
        borderRadius: '0.75rem',
        color: '#ffffff',
        fontWeight: '600', fontSize: '0.95rem',
        cursor: (googleLoading || loading) ? 'not-allowed' : 'pointer',
        opacity: (googleLoading || loading) ? 0.6 : 1,
      }}
    >
      {googleLoading ? (
        <span style={{ width: '18px', height: '18px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
      ) : (
        // Google SVG icon
        <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
          <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
          <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
          <path d="M9 3.58c1.321 0 2.508.454 3.440 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
        </svg>
      )}
      {googleLoading ? t('loading') : (isLogin ? 'Sign in with Google' : 'Sign up with Google')}
    </button>
  );

  if (showResetForm) {
    return (
      <div style={bgStyle}>
        <style>{styleTag}</style>
        <div style={{ position: 'absolute', top: '20%', left: '10%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(75,44,94,0.3) 0%, transparent 70%)', filter: 'blur(40px)', animation: 'float 8s ease-in-out infinite', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '20%', right: '10%', width: '250px', height: '250px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,128,129,0.25) 0%, transparent 70%)', filter: 'blur(40px)', animation: 'float 10s ease-in-out infinite reverse', pointerEvents: 'none' }} />

        <div style={{ maxWidth: '26rem', width: '100%', position: 'relative', zIndex: 1, animation: 'fadeInUp 0.5s ease' }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', cursor: onShowLanding ? 'pointer' : 'default' }} onClick={onShowLanding}>
            <VeylLogo width={200} centered />
          </div>
          <div style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '1.25rem', border: '1px solid rgba(255,255,255,0.08)', padding: '2rem', backdropFilter: 'blur(20px)' }}>
            {resetSent ? (
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📬</div>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.75rem', color: '#ffffff' }}>{t('resetPassword')}</h2>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.875rem', marginBottom: '1.5rem', lineHeight: '1.7' }}>{t('resetPasswordSent')}</p>
                <button className="auth-link" onClick={() => { setShowResetForm(false); setResetSent(false); setResetEmail(''); }}
                  style={{ color: '#008081', background: 'none', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem', transition: 'color 0.2s' }}>
                  ← {t('signIn')}
                </button>
              </div>
            ) : (
              <>
                <button className="auth-link" onClick={() => setShowResetForm(false)}
                  style={{ marginBottom: '1.25rem', color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.875rem', transition: 'color 0.2s', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  ← {t('back')}
                </button>
                <h2 style={{ fontSize: '1.5rem', fontWeight: '700', marginBottom: '0.4rem', color: '#ffffff' }}>{t('resetPassword')}</h2>
                <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.875rem', marginBottom: '1.75rem', lineHeight: 1.6 }}>{t('forgotPassword')}</p>
                <form onSubmit={handlePasswordReset}>
                  <div style={{ marginBottom: '1.25rem' }}>
                    <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('email')}</label>
                    <input className="auth-input" type="email" value={resetEmail} onChange={(e) => setResetEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" required />
                  </div>
                  {error && (
                    <div style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#f87171', padding: '0.75rem 1rem', borderRadius: '0.75rem', marginBottom: '1.25rem', fontSize: '0.875rem', border: '1px solid rgba(220,38,38,0.2)' }}>{error}</div>
                  )}
                  <button className="auth-btn-primary" type="submit" disabled={resetLoading || !resetEmail.trim()}
                    style={{ width: '100%', background: 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '700', border: 'none', cursor: (resetLoading || !resetEmail.trim()) ? 'not-allowed' : 'pointer', opacity: (resetLoading || !resetEmail.trim()) ? 0.5 : 1, fontSize: '0.95rem', boxShadow: '0 4px 15px rgba(0,128,129,0.3)' }}>
                    {resetLoading ? t('loading') : t('resetPassword')}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={bgStyle}>
      <style>{styleTag}</style>

      <div style={{ position: 'absolute', top: '15%', left: '8%', width: '350px', height: '350px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(75,44,94,0.3) 0%, transparent 70%)', filter: 'blur(40px)', animation: 'float 8s ease-in-out infinite', pointerEvents: 'none' }} />
      <div style={{ position: 'absolute', bottom: '15%', right: '8%', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,128,129,0.25) 0%, transparent 70%)', filter: 'blur(40px)', animation: 'float 10s ease-in-out infinite reverse', pointerEvents: 'none' }} />

      <div style={{ maxWidth: '26rem', width: '100%', position: 'relative', zIndex: 1, animation: 'fadeInUp 0.5s ease' }}>

        <div onClick={onShowLanding} style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', cursor: onShowLanding ? 'pointer' : 'default' }}>
          <VeylLogo width={200} centered />
        </div>

        <div style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: '1.25rem', border: '1px solid rgba(255,255,255,0.08)', padding: '2rem', backdropFilter: 'blur(20px)' }}>

          {/* Tabs */}
          <div style={{ display: 'flex', backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '0.75rem', padding: '0.25rem', marginBottom: '1.75rem', border: '1px solid rgba(255,255,255,0.07)' }}>
            {[
              { label: t('signIn'), value: true },
              { label: t('signUp'), value: false },
            ].map((tab) => (
              <button key={String(tab.value)} className="auth-tab"
                onClick={() => { setIsLogin(tab.value); setError(''); }}
                style={{ flex: 1, padding: '0.6rem', border: 'none', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.875rem', transition: 'background-color 0.2s, color 0.2s', backgroundColor: isLogin === tab.value ? 'rgba(0,128,129,0.25)' : 'transparent', color: isLogin === tab.value ? '#00c8c8' : 'rgba(255,255,255,0.45)' }}>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Google button */}
          <GoogleButton />

          {/* Divider */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', margin: '1.25rem 0' }}>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
            <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', fontWeight: '500' }}>or</span>
            <div style={{ flex: 1, height: '1px', backgroundColor: 'rgba(255,255,255,0.08)' }} />
          </div>

          {/* Email/password form */}
          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('email')}</label>
              <input className="auth-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} style={inputStyle} placeholder="you@example.com" required />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '0.5rem', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{t('password')}</label>
              <div style={{ position: 'relative' }}>
                <input className="auth-input" type={showPassword ? 'text' : 'password'} value={password} onChange={(e) => setPassword(e.target.value)}
                  style={{ ...inputStyle, paddingRight: '3rem' }} required minLength={8} autoComplete={isLogin ? 'current-password' : 'new-password'} />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: '0.875rem', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: 'rgba(255,255,255,0.4)', padding: '0', lineHeight: 1 }}>
                  {showPassword ? '🙈' : '👁️'}
                </button>
              </div>
              {!isLogin && (
                <p style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.4rem' }}>Minimum 8 characters</p>
              )}
            </div>

            {error && (
              <div style={{ backgroundColor: 'rgba(220,38,38,0.1)', color: '#f87171', padding: '0.75rem 1rem', borderRadius: '0.75rem', marginBottom: '1.25rem', fontSize: '0.875rem', border: '1px solid rgba(220,38,38,0.2)', lineHeight: 1.5 }}>
                {error}
              </div>
            )}

            <button className="auth-btn-primary" type="submit" disabled={loading}
              style={{ width: '100%', background: loading ? 'rgba(0,128,129,0.5)' : 'linear-gradient(135deg, #008081, #005f5f)', color: '#FFFFFF', padding: '0.875rem', borderRadius: '0.75rem', fontWeight: '700', border: 'none', cursor: loading ? 'not-allowed' : 'pointer', fontSize: '0.95rem', boxShadow: loading ? 'none' : '0 4px 15px rgba(0,128,129,0.3)', letterSpacing: '0.01em' }}>
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <span style={{ width: '14px', height: '14px', border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', display: 'inline-block', animation: 'spin 0.8s linear infinite' }} />
                  {t('loading')}
                </span>
              ) : (isLogin ? t('signIn') : t('signUp'))}
            </button>
          </form>

          <div style={{ marginTop: '1.25rem', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6rem' }}>
            {isLogin && (
              <button className="auth-link" onClick={() => { setShowResetForm(true); setError(''); }}
                style={{ color: 'rgba(255,255,255,0.35)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.8rem', transition: 'color 0.2s' }}>
                {t('forgotPassword')}
              </button>
            )}
          </div>
        </div>

        <div style={{ marginTop: '1.75rem', textAlign: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            {['🎯', '🔒', '🤝'].map((emoji, i) => (
              <span key={i} style={{ fontSize: '1.1rem' }}>{emoji}</span>
            ))}
          </div>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', lineHeight: 1.6 }}>{t('authTagline')}</p>
          <p style={{ color: 'rgba(0,200,200,0.5)', fontSize: '0.75rem', marginTop: '0.35rem', fontWeight: '500' }}>{t('authPricing')}</p>
        </div>
      </div>
    </div>
  );
}